<template>
    <b-card no-body class="snippets">
        <template slot="header">
            Snippets
            <cg-description-popover>
                <p>
                    Edit your snippets here. When you type the name in a field that supports
                    snippets and then press <kbd>Tab</kbd>, the typed name is replaced with the
                    snippet's replacement text.
                </p>

                <p>
                    To create a new snippet, click the
                    <font-awesome-icon icon="fa-solid fa-plus" />. In the popup you can set the name
                    and replacement text of the snippet. Snippet names may not contain spaces and
                    must be unique.
                </p>

                <p>
                    Click the <font-awesome-icon icon="fa-solid fa-pencil" /> button to edit an
                    existing snippet.
                </p>

                <p>Finally, <font-awesome-icon icon="fa-solid fa-trash" /> deletes a snippet.</p>
            </cg-description-popover>
        </template>
        <loader :scale="1" class="m-3" v-if="loading" />
        <div class="snippet-manager" v-else>
            <b-form-group class="filter-group">
                <input
                    :value="filter"
                    v-debounce="
                        newFilter => {
                            filter = newFilter;
                        }
                    "
                    class="form-control"
                    placeholder="Type to search snippets"
                />
            </b-form-group>

            <table class="table table-striped snippets-table">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Replacement text</th>
                        <th>
                            <b-button
                                variant="primary"
                                v-if="editable"
                                class="add-button"
                                @click="editSnippet(null)"
                                v-b-popover.hover.top="'New snippet'"
                            >
                                <font-awesome-icon icon="fa-solid fa-plus" />
                            </b-button>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="snippet in filteredSnippets" :key="snippet.id">
                        <td class="snippet-key">{{ snippet.key }}</td>
                        <td class="snippet-value">{{ snippet.value }}</td>
                        <td class="snippet-actions shrink">
                            <b-button-group
                                v-b-popover.top.hover="
                                    editable ? '' : 'You are not allowed to edit snippets.'
                                "
                            >
                                <submit-button
                                    variant="danger"
                                    class="delete-snippet-button"
                                    confirm="Are you sure you want to delete this snippet?"
                                    :disabled="!editable"
                                    :submit="() => deleteSnippet(snippet)"
                                    @after-success="afterDeleteSnippet(snippet)"
                                    v-b-popover.hover.top="'Delete snippet'"
                                >
                                    <font-awesome-icon icon="fa-solid fa-trash" />
                                </submit-button>

                                <b-button
                                    variant="primary"
                                    class="edit-snippet-button"
                                    :disabled="!editable"
                                    @click="editSnippet(snippet)"
                                    v-b-popover.hover.top="'Edit snippet'"
                                >
                                    <font-awesome-icon icon="fa-solid fa-pencil" />
                                </b-button>
                            </b-button-group>
                        </td>
                    </tr>

                    <tr v-if="filteredSnippets.length === 0">
                        <td class="p-3 text-center font-italic text-muted" colspan="3">
                            <template v-if="snippets.length === 0">
                                You have not created any snippets yet!
                            </template>
                            <template v-else> No snippets found! </template>
                        </td>
                    </tr>
                </tbody>
            </table>

            <b-modal
                :title="modalTitle"
                ref="modal"
                class="edit-snippet-modal"
                dialog-class="push-down"
                static
                @shown="focusInput"
            >
                <b-form-group v-if="editingSnippet != null">
                    <b-input-group>
                        <input
                            type="text"
                            class="form-control"
                            placeholder="Name"
                            name="snippet-key"
                            @keydown="clickSaveOnCtrlEnter"
                            v-model="editingSnippet.key"
                            ref="keyInput"
                        />
                    </b-input-group>
                </b-form-group>

                <b-form-group v-if="editingSnippet != null" class="mb-0">
                    <b-input-group>
                        <textarea
                            rows="10"
                            name="snippet-value"
                            class="form-control"
                            placeholder="Replacement text"
                            @keydown="clickSaveOnCtrlEnter"
                            v-model="editingSnippet.value"
                            ref="valueInput"
                        />
                    </b-input-group>
                </b-form-group>

                <template slot="modal-footer">
                    <b-button-toolbar justify class="flex-grow-1">
                        <b-button variant="danger" @click="cancelEditSnippet"> Cancel </b-button>

                        <submit-button
                            ref="saveButton"
                            :confirm="saveConfirmMessage"
                            :submit="saveSnippet"
                            @after-success="cancelEditSnippet"
                        >
                            Save
                        </submit-button>
                    </b-button-toolbar>
                </template>
            </b-modal>
        </div>
    </b-card>
</template>

<script>
import { mapActions } from 'vuex';

import * as models from '@/models';
import { UserStore } from '@/store';
import * as utils from '@/utils';

import Loader from './Loader';
import SubmitButton from './SubmitButton';

export default {
    name: 'snippet-manager',

    props: {
        course: {
            type: models.Course,
            default: null,
        },

        editable: {
            type: Boolean,
            default: true,
        },
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    data() {
        return {
            loading: true,
            filter: this.$route.query.filterSnippets || '',
            editingSnippet: null,
        };
    },

    computed: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        storeSnippets() {
            if (this.course) {
                return this.course.snippets;
            } else {
                return UserStore.snippets();
            }
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        snippets() {
            return Object.values(this.storeSnippets).sort((a, b) => utils.cmpNoCase(a.key, b.key));
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        filteredSnippets() {
            if (!this.filter) {
                return this.snippets;
            } else {
                const filter = this.filter.toLowerCase();
                return this.snippets.filter(
                    s => s.key.toLowerCase().match(filter) || s.value.toLowerCase().match(filter),
                );
            }
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        modalTitle() {
            if (!this.editingSnippet) {
                return '';
            } else if (this.editingSnippet.id == null) {
                return 'Add snippet';
            } else {
                return 'Edit snippet';
            }
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        baseUrl() {
            if (this.course == null) {
                return '/api/v1/';
            } else {
                return `/api/v1/courses/${this.course.id}/`;
            }
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        courseId() {
            return utils.getProps(this.course, null, 'id');
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        clickSaveOnCtrlEnter() {
            return utils.ctrlOrCmd('Enter', () => {
                this.$withRef('saveButton', el => {
                    el.onClick();
                });
            });
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        saveConfirmMessage() {
            const snippet = this.editingSnippet;

            if (snippet == null) {
                return '';
            }

            const dup = this.getSnippetWithSameKey(snippet);

            if (dup) {
                let replacement = dup.value;
                if (replacement.length > 100) {
                    replacement = `${replacement.slice(0, 100)}${utils.ELLIPSIS}`;
                }

                return (
                    'There already exists a snippet with the same name and replacement text ' +
                    `"${replacement}". Do you want to overwrite that snippet?`
                );
            } else {
                return '';
            }
        },
    },

    methods: {
        ...mapActions({
            updateCourse: 'courses/updateCourse',
        }),

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        editSnippet(snippet) {
            if (snippet == null) {
                this.editingSnippet = {
                    key: '',
                    value: '',
                };
            } else {
                this.editingSnippet = { ...snippet };
            }

            this.$refs.modal.show();
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        cancelEditSnippet() {
            this.$refs.modal.hide();
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        ensureValidSnippet(snippet) {
            const { key, value } = snippet;

            if (!key) {
                throw new Error('The snippet name may not be empty.');
            }

            if (key.match(/\s/)) {
                throw new Error(
                    'Snippet names may not contain spaces. If you want to use ' +
                        'a multiple words in a name you can separate them with a ' +
                        'dash or an underscore.',
                );
            }

            if (!value) {
                throw new Error('The snippet replacement text may not be empty.');
            }
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        getSnippetWithSameKey(snippet) {
            const { id, key } = snippet;

            return this.snippets.find(s => s.key === key && s.id !== id);
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        isSnippetUnchanged(snippet) {
            const { id, key, value } = snippet;

            const oldSnippet = this.snippets.find(s => s.id === id);

            return oldSnippet && key === oldSnippet.key && value === oldSnippet.value;
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        saveSnippet() {
            const snippet = this.editingSnippet;

            this.ensureValidSnippet(snippet);

            if (this.isSnippetUnchanged(snippet)) {
                return Promise.resolve(snippet);
            }

            const dup = this.getSnippetWithSameKey(snippet);
            if (dup) {
                const oldSnippet = { ...snippet };
                snippet.id = dup.id;
                let req = this.updateSnippet(snippet);
                if (oldSnippet.id != null) {
                    req = req
                        .then(() => this.deleteSnippet(oldSnippet))
                        .then(this.afterDeleteSnippet);
                }
                return req;
            } else if (snippet.id == null) {
                return this.addSnippet(snippet);
            } else {
                return this.updateSnippet(snippet);
            }
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        addSnippet(snippet) {
            return this.$http.put(this.getSnippetRoute('snippet'), snippet).then(response => {
                snippet.id = response.data.id;
                if (this.courseId) {
                    return this.updateCourse({
                        courseId: this.courseId,
                        courseProps: {
                            snippets: [...this.course.snippets, snippet],
                        },
                    });
                } else {
                    return UserStore.addSnippet(snippet);
                }
            });
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        updateSnippet(snippet) {
            return this.$http
                .patch(this.getSnippetRoute(`snippets/${snippet.id}`), snippet)
                .then(() => {
                    if (this.courseId) {
                        return this.updateCourse({
                            courseId: this.courseId,
                            courseProps: {
                                snippets: this.course.snippets.map(snip =>
                                    snip.id === snippet.id ? snippet : snip,
                                ),
                            },
                        });
                    } else {
                        return UserStore.updateSnippet(snippet);
                    }
                });
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        deleteSnippet(snippet) {
            return this.$http
                .delete(this.getSnippetRoute(`snippets/${snippet.id}`))
                .then(() => snippet);
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        afterDeleteSnippet(snippet) {
            if (this.courseId) {
                return this.updateCourse({
                    courseId: this.course.id,
                    courseProps: {
                        snippets: this.course.snippets.filter(snip => snip.id !== snippet.id),
                    },
                });
            } else {
                return UserStore.deleteSnippet(snippet);
            }
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        getSnippetRoute(routeEnd) {
            return `${this.baseUrl}${routeEnd}`;
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        refreshSnippets() {
            if (this.courseId) {
                return this.$http.get(`/api/v1/courses/${this.courseId}/snippets/`, ({ data }) => {
                    this.updateCourse({
                        courseId: this.courseId,
                        courseProps: data,
                    });
                    return data;
                });
            } else {
                return UserStore.refreshSnippets();
            }
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async focusInput() {
            if (this.editingSnippet.id == null) {
                const keyInput = await this.$waitForRef('keyInput');
                if (keyInput != null) {
                    keyInput.focus();
                }
            } else {
                const valueInput = await this.$waitForRef('valueInput');
                if (valueInput != null) {
                    valueInput.focus();
                }
            }
        },
    },

    watch: {
        courseId: {
            immediate: true,
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            async handler() {
                await this.refreshSnippets();
                this.loading = false;
            },
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        filter() {
            const newQuery = { ...this.$route.query };
            newQuery.filterSnippets = this.filter || undefined;

            this.$router.replace({
                query: newQuery,
                hash: this.$route.hash,
            });
        },
    },

    components: {
        Loader,
        SubmitButton,
    },
};
</script>

<style lang="less" scoped>
.filter-group {
    padding: 0.75rem;
    margin-bottom: 0;
}

.snippets-table {
    margin-bottom: 0;

    th,
    td {
        vertical-align: middle;
    }

    .snippet-key {
        width: 25%;
    }

    .snippet-value {
        width: 75%;
        max-width: 0px;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
    }
}

.add-button {
    width: 100%;
}
</style>

<style lang="less">
.edit-snippet-modal {
    position: initial !important;
}
</style>
